import $ from 'jquery';
import { BaseController } from '../core/BaseController';
import { IS_ACTIVE_CLASS } from '../constants/classes';
import { IS_FOCUS_ACTIVE_CLASS } from '../constants/classes';

/**
 * An object of selectors used in this file
 * @property SELECTORS
 * @type {Object}
 * @final
 */
const SELECTORS = {
    MOBILE_MENU_TRIGGER: '.js-navigationView-mobileTrigger',
    NAV_CONTAINER: '.js-navigationView',
    NAV_PRIMARY: '.js-navigationView-nav',
    NAV_USER_DROPDOWN: '.js-navigationView-userDropdown',
    SEARCH_INPUT: '.js-searchInput',
    SEARCH_SUBMIT: '.js-searchSubmit'
};

/**
 * Navigation functionality across breakpoints
 *
 * @class NavigationView.
 * @constructor
 */
export default class NavigationView extends BaseController {
    constructor($element) {
        super();
        this.init($element);
    }

    /**
     * Initializes methods
     *
     * @method init
     * @public
     * @chainable
     */
    init($element) {
        /**
         * A reference to the containing DOM element
         *
         * @property propertyName
         * @type {type}
         * @public
         */
        this.$element = $element;

        /**
         * Tracks if the desktop event binding is enabled
         *
         * @default false
         * @property isDesktopEnabled
         * @type {Boolean}
         * @public
         */
        this.isDesktopEnabled = false;

        /**
         * Tracks if the mobile event binding is enabled
         *
         * @default false
         * @property isMobileEnabled
         * @type {Boolean}
         * @public
         */
        this.isMobileEnabled = false;

        return this.setupHandlers()
            .createChildren()
            .layout()
            .enable();
    }

    /**
     * SetupHandlers
     *
     * @method setupHandlers
     * @public
     * @chainable
     */
    setupHandlers() {
        this.onMobileToggleHandler = this.onMobileToggle.bind(this);;
        this.onMobileChildFocusHandler = this.onMobileChildFocus.bind(this);
        this.onMobileChildFocusOutHandler = this.onMobileChildFocusOut.bind(this);
        this.onMobileMenuOffClickHandler = this.onMobileMenuOffClick.bind(this);
        this.onSearchInputKeydownHandler = this.onSearchInputKeydown.bind(this);
        this.onSearchSubmitHandler = this.onSearchSubmit.bind(this);
        return this;
    }

    /**
     * createChildren
     *
     * @method createChildren
     * @public
     * @chainable
     */
    createChildren() {
        this.$primaryNav = this.$element.find(SELECTORS.NAV_PRIMARY);
        this.$tabbableChildren = this.$primaryNav.find('a,button');
        this.currentBreakpoint = this.getCurrentSize();
        this.$mobileToggle = this.$element.find(SELECTORS.MOBILE_MENU_TRIGGER);
        this.$searchInput = this.$element.find(SELECTORS.SEARCH_INPUT);
        this.$searchSubmit = this.$element.find(SELECTORS.SEARCH_SUBMIT);

        return this;
    }

    /**
     * Creates children specific to mobile
     *
     * @method createMobileChildren
     * @public
     * @chainable
     */
    createMobileChildren() {
        this.$body = $('body');

        return this;
    }

    /**
     * Makes nulls out cached mobile elements
     *
     * @method removeMobileChildren
     * @public
     * @chainable
     */
    removeMobileChildren() {
        // this.$mobileToggle = null;
        this.$body = null;

        return this;
    }

    /**
     * Layout
     *
     * @method layout
     * @public
     * @chainable
     */
    layout() {

        if (!this.isMobile()) {
            this.disableMobile()
                .removeMobileChildren()
                //     .enableDesktop();
        } else {
            // this.disableDesktop()
            this.createMobileChildren()
                .enableMobile();
        }

        return this;
    }

    /**
     * onMobileToggle
     *
     * When the mobile navigation is expanded/toggled
     *
     * @method onMobileToggle
     * @public
     * @chainable
     */
    onMobileToggle(event) {

        if (this.$element.hasClass(IS_ACTIVE_CLASS)) {
            this.closeMobileMenu();

        } else {
            this.$element.addClass(IS_ACTIVE_CLASS);
            this.$mobileToggle.attr('aria-expanded', 'true');
            this.$primaryNav.attr('aria-hidden', 'false');
        }

        return this;
    }

    /**
     * Determines when user is clicking off of the main menu
     * in mobile
     *
     * @method onMobileMenuOffClick
     * @public
     * @chainable
     */
    onMobileMenuOffClick(e) {
        if (this.$element.find(e.target).length) {
            return
        } else {
            this.closeMobileMenu();
        }
    }

    /**
     * If the user pressed the Enter key inside the search input, submit the search query
     *  
     * @method onSearchInputKeydown
     * @public
     */
     onSearchInputKeydown(event) {
        if (event && event.originalEvent && event.originalEvent.key && event.originalEvent.key.toLowerCase() === "enter") {
            this.sendSearch();
        }
    }

    /**
     * If the user pressed the search button, submit the search query
     *  
     * @method onSearchSubmit
     * @public
     */
     onSearchSubmit(event) {
        this.sendSearch();
    }

    /**
     * Redirects user to the search page with the value of the search input appended as the search term
     *  
     * @method sendSearch
     * @public
     */
    sendSearch(){
        this.searchUrlFormat = this.$searchSubmit.attr('data-search-url-format');
        this.searchTerm = this.$searchInput.val();
        window.location = this.searchUrlFormat.replace("{0}", this.searchTerm);
    }

    /**
     * Removes all active classes on the main and
     * subnavigation layers
     *
     * @method closeMobileMenu
     * @public
     * @chainable
     */
    closeMobileMenu() {
        this.$element.removeClass(IS_FOCUS_ACTIVE_CLASS).removeClass(IS_ACTIVE_CLASS);
        this.$mobileToggle.attr('aria-expanded', 'false');
        this.$primaryNav.attr('aria-hidden', 'true');
        return this;
    }

    /**
     * Apply aria when menu 
     * is expanded
     *
     * @method ariaExpanded
     * @public
     * @chainable
     */
    ariaExpanded() {
        this.$mobileToggle.attr('aria-expanded', 'true');
        this.$primaryNav.attr('aria-hidden', 'false');
        return this;
    }

    /**
     * Apply aria when menu 
     * is collapsed
     *
     * @method ariaCollapsed
     * @public
     * @chainable
     */
    ariaCollapsed() {
        this.$mobileToggle.attr('aria-expanded', 'false');
        this.$primaryNav.attr('aria-hidden', 'true');
        return this;
    }

    /**
     * onMobileChildFocus: Handles display of the menu
     * when any tabbable menu item has focus
     *
     * @method onMobileChildFocus
     * @public
     * @chainable
     */
    onMobileChildFocus(e) {
        if (!this.isMobile()) {
            return;
        }
        this.$element.addClass(IS_FOCUS_ACTIVE_CLASS);
        this.ariaExpanded();
        return this;
    }

    /**
     * onMobileChildFocusOut: Handles hiding of the menu
     * when no menu children have focus
     *
     * @method onMobileChildFocusOut
     * @public
     * @chainable
     */
    onMobileChildFocusOut(e) {
        if (!this.isMobile()) {
            return;
        }

        this.$element.removeClass(IS_FOCUS_ACTIVE_CLASS);

        if (!this.$element.hasClass(IS_ACTIVE_CLASS)) {
            this.ariaCollapsed();
        }

        return this;
    }

    /**
     * On Breakpoint Change
     *
     * @method onBreakpointChange
     * @public
     * @chainable
     */
    onBreakpointChange(breakpoint) {
        /* Update our local property with new breakpoint value */
        this.currentBreakpoint = breakpoint.currentSize;

        this.layout();
    }

    /**
     * Handles resetting
     * the mobile menu aria and
     * active classes
     *
     * @method resetMobile
     * @public
     * @chainable
     */
    resetMobile() {
        this.ariaExpanded();
        this.$element.removeClass(IS_FOCUS_ACTIVE_CLASS).removeClass(IS_ACTIVE_CLASS);
    }

    /**
     * Handles reestablishing
     * the mobile menu aria and
     * active classes
     *
     * @method setMobileMenu
     * @public
     * @chainable
     */
    setMobile() {
        this.ariaCollapsed();
        this.$element.removeClass(IS_FOCUS_ACTIVE_CLASS).removeClass(IS_ACTIVE_CLASS);
    }

    /**
     * Enable
     *
     * @method enable
     * @public
     * @chainable
     */
    enable() {
        this.$tabbableChildren.on('focus', this.onMobileChildFocusHandler);
        this.$tabbableChildren.on('focusout', this.onMobileChildFocusOutHandler);
        this.$searchInput.on('keydown', this.onSearchInputKeydownHandler);
        this.$searchSubmit.on('click', this.onSearchSubmitHandler);

        return this;
    }

    /**
     * Disable
     *
     * @method disable
     * @public
     * @chainable
     */
    disable() {
        this.$tabbableChildren.off('focus', this.onMobileChildFocusHandler);
        this.$tabbableChildren.off('focusout', this.onMobileChildFocusOutHandler);
        this.$searchInput.off('keydown', this.onSearchInputKeydownHandler);
        this.$searchSubmit.off('click', this.onSearchSubmitHandler);

        return this;
    }

    /**
     * Enabling mobile events when on mobile
     *
     * @method enableMobile
     * @public
     * @chainable
     */
    enableMobile() {
        if (this.isMobileEnabled) {
            return this;
        }
        this.$mobileToggle.on('click', this.onMobileToggleHandler);
        this.$body.on('click touchstart', this.onMobileMenuOffClickHandler);

        this.isMobileEnabled = true;
        this.setMobile();

        return this;
    }

    /**
     * Disabling mobile events when on desktop
     *
     * @method methodName
     * @public
     * @chainable
     */
    disableMobile() {
        if (!this.isMobileEnabled) {
            this.ariaExpanded();
            return this;
        }

        this.$mobileToggle.off('click', this.onMobileToggleHandler);
        this.$body.off('click', this.onMobileMenuOffClickHandler);

        this.isMobileEnabled = false;
        this.resetMobile();

        return this;
    }
}